export interface ValidationError {
  readonly message: string;
  readonly path: string;
  readonly problem: string;
  readonly source: string;
}

export interface ValidationFailure {
  readonly error: ValidationError;
  readonly ok: false;
}

export interface ValidationSuccess<T> {
  readonly ok: true;
  readonly value: T;
}

export type ValidationResult<T> = ValidationFailure | ValidationSuccess<T>;

export type JsonPrimitive = boolean | null | number | string;
export type JsonValue =
  | JsonPrimitive
  | readonly JsonValue[]
  | { readonly [key: string]: JsonValue };
export type JsonObject = Readonly<Record<string, JsonValue>>;

export function validationFailure(
  source: string,
  path: string,
  problem: string,
): ValidationFailure {
  return {
    ok: false,
    error: {
      source,
      path,
      problem,
      message: `${source}: ${path}: ${problem}`,
    },
  };
}

export function validationSuccess<T>(value: T): ValidationSuccess<T> {
  return { ok: true, value };
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function isStringRecord(
  value: unknown,
): value is Record<string, string> {
  return (
    isRecord(value) &&
    Object.values(value).every((entry) => typeof entry === "string")
  );
}

function isJsonValueWithAncestors(
  value: unknown,
  ancestors: Set<object>,
): value is JsonValue {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return true;
  }
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "object" || ancestors.has(value)) return false;

  ancestors.add(value);
  const entries = Array.isArray(value) ? value : Object.values(value);
  const valid = entries.every((entry) =>
    isJsonValueWithAncestors(entry, ancestors),
  );
  ancestors.delete(value);
  return valid;
}

export function isJsonValue(value: unknown): value is JsonValue {
  return isJsonValueWithAncestors(value, new Set<object>());
}

export function isJsonObject(value: unknown): value is JsonObject {
  return isRecord(value) && isJsonValue(value);
}
